"use client";

import { useActionState, useMemo, useState } from "react";
import { useSearchParams } from "next/navigation";
import { VStack } from "@astryxdesign/core/VStack";
import { HStack } from "@astryxdesign/core/HStack";
import { Card } from "@astryxdesign/core/Card";
import { Heading } from "@astryxdesign/core/Heading";
import { Text } from "@astryxdesign/core/Text";
import { TextInput } from "@astryxdesign/core/TextInput";
import { CheckboxInput } from "@astryxdesign/core/CheckboxInput";
import { Button } from "@astryxdesign/core/Button";
import { Link } from "@astryxdesign/core/Link";
import { Banner } from "@astryxdesign/core/Banner";
import { useResolvedDarkMode } from "@/lib/theme/use-resolved-dark";
import { loginAction, type FormState } from "@/app/(auth)/actions";
import {
  OAuthDivider,
  OAuthSignInButtons,
} from "@/components/auth/oauth-sign-in";
import { WEBMCP_PARAMS, webmcpParam } from "@/lib/webmcp/forms";
import { PromoAttachedBanner } from "@/components/promo/promo-attached-banner";
import { PROMO_QUERY_KEY } from "@/lib/billing/promo-link";

const initialState: FormState = { status: "idle" };

const VK_ERROR_MESSAGES: Record<string, string> = {
  vk_disabled: "Вход через VK ID временно недоступен.",
  vk_denied: "Вход через VK ID отменён.",
  vk_state: "Сессия VK ID устарела. Попробуйте войти ещё раз.",
  vk_signin: "Не удалось завершить вход через VK ID.",
  vk_failed: "Ошибка входа через VK ID. Попробуйте позже.",
  "2fa_expired":
    "Сессия подтверждения истекла. Войдите снова — мы отправим новый код.",
};

type LoginFormProps = {
  yandexEnabled: boolean;
  vkEnabled: boolean;
};

export function LoginForm({ yandexEnabled, vkEnabled }: LoginFormProps) {
  const [state, formAction, pending] = useActionState(loginAction, initialState);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(false);
  const isDark = useResolvedDarkMode();
  const searchParams = useSearchParams();

  const oauthError = useMemo(() => {
    const code = searchParams.get("error");
    if (!code) return null;
    return VK_ERROR_MESSAGES[code] ?? null;
  }, [searchParams]);

  const oauthEnabled = yandexEnabled || vkEnabled;
  const oauthHint = [
    yandexEnabled ? "Яндекс ID" : null,
    vkEnabled ? "VK ID" : null,
  ]
    .filter(Boolean)
    .join(" или ");

  const promoCode = searchParams.get(PROMO_QUERY_KEY)?.trim();
  const registerHref = promoCode
    ? `/register?${PROMO_QUERY_KEY}=${encodeURIComponent(promoCode)}`
    : "/register";

  return (
    <>
      <VStack gap={2} align="start">
        <Heading level={1} type="display-3">
          Вход
        </Heading>
        <Text color="secondary">
          {oauthEnabled
            ? `Войдите по email или через ${oauthHint}.`
            : "Доступ к отчётам, истории и сравнению исследований."}
        </Text>
      </VStack>

      <PromoAttachedBanner variant="form" />

      {oauthError ? (
        <Banner status="error" title={oauthError} container="card" />
      ) : null}

      {state.status === "error" && state.message ? (
        <Banner status="error" title={state.message} container="card" />
      ) : null}

      <Card padding={6} width="100%">
        <VStack gap={5} align="stretch" width="100%">
          <form
            action={formAction}
            className="w-full"
            toolname="signInWithEmail"
            tooldescription="Войти в Идеалитика по email и паролю. Для доступа к отчётам и кабинету."
          >
            <VStack gap={4} width="100%">
              <TextInput
                type="email"
                label="Email"
                htmlName="email"
                value={email}
                onChange={setEmail}
                placeholder="you@company.ru"
                status={
                  state.fieldErrors?.email
                    ? { type: "error", message: state.fieldErrors.email }
                    : undefined
                }
                isRequired
                hasAutoFocus
                {...webmcpParam(WEBMCP_PARAMS.accountEmail)}
              />
              <TextInput
                type="password"
                label="Пароль"
                htmlName="password"
                value={password}
                onChange={setPassword}
                status={
                  state.fieldErrors?.password
                    ? { type: "error", message: state.fieldErrors.password }
                    : undefined
                }
                isRequired
                {...webmcpParam(WEBMCP_PARAMS.accountPassword)}
              />
              <input
                type="hidden"
                name="remember"
                value={remember ? "true" : "false"}
                {...webmcpParam(WEBMCP_PARAMS.rememberSession)}
              />
              <HStack justify="between" vAlign="center" width="100%">
                <CheckboxInput
                  label="Запомнить меня"
                  value={remember}
                  onChange={setRemember}
                />
                <Link href="/forgot-password">Забыли пароль?</Link>
              </HStack>
              <Button
                label="Войти"
                variant={isDark ? "primary" : "secondary"}
                type="submit"
                size="lg"
                isLoading={pending}
              />
            </VStack>
          </form>

          {oauthEnabled ? (
            <VStack gap={3} align="stretch" width="100%">
              <OAuthDivider />
              <OAuthSignInButtons
                yandexEnabled={yandexEnabled}
                vkEnabled={vkEnabled}
              />
            </VStack>
          ) : null}
        </VStack>
      </Card>

      <HStack gap={1} justify="center">
        <Text color="secondary">Нет аккаунта?</Text>
        <Link href={registerHref}>Регистрация</Link>
      </HStack>
    </>
  );
}
